import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { UserRole, NotificationPreferences } from '@/types'

export function useAuth() {
  const store = useAuthStore()
  const router = useRouter()

  const user = computed(() => store.user)
  const isAuthenticated = computed(() => store.isAuthenticated)
  const isLoading = computed(() => store.isLoading)
  const error = computed(() => store.error)
  const initialized = computed(() => store.initialized)

  // Role checks
  const isSuperAdmin = computed(() => store.isSuperAdmin)
  const isTenantAdmin = computed(() => store.isTenantAdmin)
  const isAdmin = computed(() => store.isAdmin)
  const isOperator = computed(() => store.isOperator)
  const isClient = computed(() => store.isClient)
  const isMember = computed(() => store.isMember)
  const isOrganizationOwner = computed(() => store.isOrganizationOwner)
  const hasInternalAccess = computed(() => store.hasInternalAccess)

  // Permission checks
  const canManageUsers = computed(() => store.canManageUsers)
  const canManageOrganizations = computed(() => store.canManageOrganizations)
  const canManageCRM = computed(() => store.canManageCRM)
  const canManageSalesOrders = computed(() => store.canManageSalesOrders)
  const canManageSettings = computed(() => store.canManageSettings)
  const canCreateProjects = computed(() => store.canCreateProjects)
  const canDeleteProjects = computed(() => store.canDeleteProjects)
  const canAssignOperators = computed(() => store.canAssignOperators)
  const canManageTenants = computed(() => store.canManageTenants)
  const canManageOrganizationMembers = computed(() => store.canManageOrganizationMembers)

  // Tenant & impersonation
  const currentTenant = computed(() => store.currentTenant)
  const tenantFeatures = computed(() => store.tenantFeatures)
  const isImpersonating = computed(() => store.isImpersonating)
  const effectiveTenantId = computed(() => store.effectiveTenantId)
  const isModuleEnabled = computed(() => store.isModuleEnabled)

  const login = async (email: string, password: string) => {
    await store.login({ email, password })
    redirectAfterLogin()
  }

  const loginWithGoogle = async () => {
    await store.loginWithGoogle()
    redirectAfterLogin()
  }

  const register = async (email: string, password: string, displayName: string, companyName?: string) => {
    await store.register({ email, password, displayName, companyName })
    redirectAfterLogin()
  }

  const logout = async () => {
    await store.logout()
    router.push('/login')
  }

  const resetPassword = async (email: string) => {
    await store.resetPassword(email)
  }

  const updatePreferences = async (preferences: { language?: 'es' | 'en'; theme?: 'light' | 'dark'; notificationPreferences?: NotificationPreferences }) => {
    await store.updateUserPreferences(preferences)
  }

  const updateProfile = async (profile: { displayName?: string; phone?: string }) => {
    await store.updateUserProfile(profile)
  }

  const clearError = () => {
    store.clearError()
  }

  const startImpersonation = async (tenantId: string) => {
    await store.startImpersonation(tenantId)
  }

  const stopImpersonation = async () => {
    await store.stopImpersonation()
  }

  // Multi-tenant (Modelo C): cambiar de tenant activo entre los del usuario.
  const selectTenant = async (tenantId: string) => {
    await store.selectTenant(tenantId)
  }

  const fetchSelectableTenants = () => store.fetchSelectableTenants()

  // ¿El usuario puede cambiar de tenant? (multi-tenant no-superadmin con >1 en la allowed-list)
  const canSwitchTenant = computed(
    () => !store.isSuperAdmin && (store.user?.tenantIds?.length ?? 0) > 1,
  )

  const redirectAfterLogin = () => {
    const role = store.userRole
    switch (role) {
      case 'superadmin':
      case 'tenant_admin':
      case 'admin':
        router.push('/dashboard')
        break
      case 'operator':
        router.push('/dashboard')
        break
      case 'client':
      case 'member':
        router.push('/dashboard')
        break
      default:
        router.push('/')
    }
  }

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    const userRole = store.userRole
    if (!userRole) return false
    if (Array.isArray(roles)) {
      return roles.includes(userRole)
    }
    return userRole === roles
  }

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return hasRole(roles)
  }

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    initialized,

    // Role checks
    isSuperAdmin,
    isTenantAdmin,
    isAdmin,
    isOperator,
    isClient,
    isMember,
    isOrganizationOwner,
    hasInternalAccess,

    // Permission checks
    canManageUsers,
    canManageOrganizations,
    canManageCRM,
    canManageSalesOrders,
    canManageSettings,
    canCreateProjects,
    canDeleteProjects,
    canAssignOperators,
    canManageTenants,
    canManageOrganizationMembers,

    // Tenant & impersonation
    currentTenant,
    tenantFeatures,
    isImpersonating,
    effectiveTenantId,
    isModuleEnabled,
    canSwitchTenant,

    // Actions
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updatePreferences,
    updateProfile,
    clearError,
    hasRole,
    hasAnyRole,
    startImpersonation,
    stopImpersonation,
    selectTenant,
    fetchSelectableTenants,
  }
}
